import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdActionsSelect, CmdLabel, CmdSimpleSelect } from '@commander-services/gui-components';
import { useRecoilValue } from 'recoil';
import { Controller, useForm } from 'react-hook-form';
import FormikValidatorService from '../../Services/FormikValidatorService';
import configJs from '../../../config';
import PermissionsService, {
  PERMISSION_IMPORTING_TEMPLATE,
} from '../../Services/PermissionsService';
import CmdUploadHookForm from '../Forms/CmdUploadHookForm';
import { transformErrors } from '../../Services/HookFormService';
import { customersAtom, ICustomer } from '../../store/recoil/customers';

interface IWizardInputForm {
  customerId: string;
  file: any;
  importTemplateId: string;
}

interface IFormProps {
  onChangeCallback: (values: any) => void;
  data: any;
  isValidated: boolean;
  validationCallback: (value: boolean) => void;
  handleFavoriteGroupSelect: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  handleDownloadGroupSelect: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

export default function Form(props: IFormProps): JSX.Element {
  const allCustomerData = useRecoilValue<ICustomer[]>(customersAtom);
  const { formatMessage: f } = useIntl();

  // fields data
  const customerId = props.data.filter((i: any) => i.indexId === 'customerId')[0];
  const file = props.data.filter((i: any) => i.indexId === 'file')[0];
  const importTemplateId = props.data.filter((i: any) => i.indexId === 'importTemplateId')[0];

  const validateFields = (values) => {
    let formErrors = {};
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, {
      customerId:
        customerId && customerId.rules
          ? customerId.rules.filter((i) => i.param !== 'assertTrue')
          : [],
      file: file && file.rules ? file.rules : [],
      importTemplateId: [{ param: 'required', value: true, message: f({ id: 'form.required' }) }],
    });
    const allErrors = transformErrors(formErrors);
    if (Object.keys(allErrors).length > 0) {
      props.validationCallback(false);
    }
    return allErrors;
  };

  const {
    watch,
    getValues,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<IWizardInputForm>({
    mode: 'onChange',
    defaultValues: {
      customerId: customerId?.value || '',
      file: file?.value ? [file.value] : '',
      importTemplateId: importTemplateId?.value || '',
    },
    resolver: async (data) => {
      const formErrors = validateFields(data);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const handleCustomerChange = (value: string | number) => {
    setValue('customerId', String(value));
    const values = getValues();
    values.customerId = String(value);
    if (watch('file')) {
      values.file = watch('file')[0];
    }
    props.onChangeCallback(values);
    trigger('customerId');
  };

  const handleFileChange = (value) => {
    const values = getValues();
    values.importTemplateId = '';
    setValue('importTemplateId', '');
    values.file = value[0];
    setValue('file', value);
    props.onChangeCallback(values);
    trigger('file');
  };

  const handleTemplateChange = (value: string | number) => {
    if (value !== watch('importTemplateId')) {
      setValue('importTemplateId', String(value));
      const values = getValues();
      if (watch('file')) {
        values.file = watch('file')[0];
      }
      values.importTemplateId = String(value);
      props.onChangeCallback(values);
      trigger('importTemplateId');
    }
  };

  React.useEffect(() => {
    if (props.isValidated) {
      trigger();
      const values = getValues();
      const formErrors = validateFields(values);
      if (Object.keys(formErrors).length === 0) {
        props.validationCallback(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isValidated]);

  React.useEffect(() => {
    if (customerId && customerId.values && customerId.values.length === 1) {
      setValue('customerId', customerId.values[0].value);
    } else if (customerId && customerId.value && !customerId.values) {
      setValue('customerId', customerId.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const prepareActions = () => {
    const options = importTemplateId && importTemplateId.values ? importTemplateId.values : [];
    const groupNames = Object.keys(options);
    groupNames.map((name: string) => {
      const groupValues = options[name];
      groupValues.map((item: any) => {
        item.actions = [];
        if (item.data && item.data.isFavorite === false) {
          const favAction = {
            title: f({ id: 'wizard.importTemplateId.favorite.tooltip' }),
            icon: 'cmd-icons cmd-icon-favourite-empty',
            actionCallback: props.handleFavoriteGroupSelect,
          };

          if (
            PermissionsService.hasAccess(PERMISSION_IMPORTING_TEMPLATE, ['read'], customerId.value)
          ) {
            const downAction = {
              title: f({ id: 'wizard.importTemplateId.download.tooltip' }),
              icon: 'cmd-icons cmd-icon-download-3',
              actionCallback: props.handleDownloadGroupSelect,
            };
            item.actions.push(downAction);
          }

          item.actions.push(favAction);
        }

        if (item.data && item.data.isFavorite === true) {
          const favAction = {
            title: f({ id: 'wizard.importTemplateId.favorite.tooltip' }),
            icon: 'cmd-icons cmd-icon-favourite-full',
            actionCallback: props.handleFavoriteGroupSelect,
          };

          if (
            PermissionsService.hasAccess(PERMISSION_IMPORTING_TEMPLATE, ['read'], customerId.value)
          ) {
            const downAction = {
              title: f({ id: 'wizard.importTemplateId.download.tooltip' }),
              icon: 'cmd-icons cmd-icon-download-3',
              actionCallback: props.handleDownloadGroupSelect,
            };
            item.actions.push(downAction);
          }

          item.actions.push(favAction);
        }
      });
    });

    return options;
  };

  const extractExtensionsFromPropsData = (): string[] => {
    const fileField = props.data.find((field) => field.indexId === 'file');

    if (fileField && fileField.rules) {
      const extensionsRule = fileField.rules.find((rule) => rule.param === 'extensions');

      return extensionsRule ? extensionsRule.value : ['txt', 'csv', 'xls', 'xlsx', 'dbs'];
    }

    return ['txt', 'csv', 'xls', 'xlsx', 'dbs'];
  };

  return (
    <div>
      {customerId?.values && customerId.values.length > 1 ? (
        <Controller
          name="customerId"
          control={control}
          rules={{
            required: errors.customerId ? errors.customerId.message : undefined,
          }}
          render={({ field }) => (
            <CmdSimpleSelect
              {...field}
              id="customerId"
              name="customerId"
              label={customerId && customerId.title ? customerId.title : ''}
              introOption={f({ id: 'form.choose' })}
              options={customerId && customerId.values ? customerId.values : []}
              required={true}
              onChangeCallback={handleCustomerChange}
              touched
              error={errors.customerId?.message}
              value={watch('customerId')}
            />
          )}
        />
      ) : (
        <CmdLabel
          title={f({ id: 'enum.waypoints.category.customer' })}
          value={
            customerId?.values && customerId.values.length === 1
              ? customerId.values[0].item
              : allCustomerData.find((item) => item.id === customerId?.value)?.name
          }
        />
      )}
      {watch('customerId') ? (
        <Controller
          name="file"
          control={control}
          render={({ field }) => (
            <CmdUploadHookForm
              {...field}
              id="file"
              label={file && file.title ? file.title : ''}
              required={true}
              dnd={true}
              control={control}
              number={1}
              maxFileSize={6}
              acceptedFileTypes={extractExtensionsFromPropsData()}
              url={`${configJs.Backend.url}/api/v1/file/upload${
                watch('customerId') || (customerId && customerId.value)
                  ? `?customerId=${watch('customerId') || customerId.value}`
                  : ''
              }`}
              onChangeCallback={handleFileChange}
            />
          )}
        />
      ) : (
        <CmdLabel
          title={file && file.title ? file.title : ''}
          value={f({ id: 'waypoints.form.customer.pick' })}
        />
      )}
      {watch('file') ? (
        <Controller
          name="importTemplateId"
          control={control}
          rules={{
            required: errors.importTemplateId ? errors.importTemplateId.message : undefined,
          }}
          render={({ field }) => (
            <CmdActionsSelect
              {...field}
              id="importTemplateId"
              name="importTemplateId"
              required={true}
              label={importTemplateId && importTemplateId.title ? importTemplateId.title : ''}
              options={prepareActions()}
              setValue={handleTemplateChange}
              placeholder={
                importTemplateId && importTemplateId.placeholder ? importTemplateId.placeholder : ''
              }
              searchPlaceholder={f({ id: 'form.search' })}
              disabled={
                importTemplateId && importTemplateId.disabled ? importTemplateId.disabled : false
              }
              touched
              error={errors.importTemplateId?.message}
            />
          )}
        />
      ) : (
        <CmdLabel
          title={importTemplateId && importTemplateId.title ? importTemplateId.title : ''}
          value={f({ id: 'form.placeholders.expenseImports.noSuitableTemplate' })}
        />
      )}
    </div>
  );
}
